import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { BattingRankingStats, DataSection, PlateAppearanceResult, ResultDataNew, ScoringData, TaggingData, RankingScopeContext, PitchingRankingStats } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./../MultiRankingStatCalculator.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import ClassifiedPitchesFilter from "../../../filters/contextual/ClassifiedPitchesFilter.js";
import ContextualFilterBase from "../../../filters/contextual/ContextualFilterBase.js";
import * as PlateAppearanceResultClassifiers from '../../../classificators/plateAppearanceResult/index.js';
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import resultsSliceBeforePitchType from "../../../classificators/resultsSlice/resultsSliceBeforePitchType.js";
import ResultPair from "../../../valueObjects/ResultPair.js";
import classifiesAsAirBall from "../../../classificators/battedBallType/classifiesAsAirBall.js";

class NewSacrificeStatsCalculator extends MultiRankingStatCalculator{
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter,
        sacrificeClassifier, airBallClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (typeof(sacrificeClassifier) !== 'undefined' && typeof(sacrificeClassifier) !== 'function') {
            throw new TypeError('sacrificeClassifier must be an function or undefined');
        }
        if (typeof(airBallClassifier) !== 'undefined' && typeof(airBallClassifier) !== 'function') {
            throw new TypeError('airBallClassifier must be an function or undefined');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            context === RankingScopeContext.Batting.key ? [
                BattingRankingStats.SacrificeFlies, BattingRankingStats.SacrificeHits,
            ] : [
                PitchingRankingStats.SacrificeFlies, PitchingRankingStats.SacrificeHits,
            ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    context === RankingScopeContext.Batting.key ? battingScopeContext: pitchingScopeContext,
                    resultsSliceBeforePitchType
                )
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
        this.sacrificeClassifier = sacrificeClassifier || PlateAppearanceResultClassifiers.classifiesAsSacrifice;
        this.airBallClassifier = airBallClassifier || classifiesAsAirBall;
        this.context = context;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const results = slice.getSmallestSlice();

        const output = {};
        let sac = 0;
        let sacHits = 0;
        let sacFlies = 0;

        for (let r = 0; r < results.length; r++) {
            const result = results[r];
            
            if (result && result[ResultDataNew.Data.key] && result[ResultDataNew.Data.key][DataSection.Scoring.key]) {
                const paResult = result[ResultDataNew.Data.key][DataSection.Scoring.key][ScoringData.PlateAppearanceResult.key];

                if (this.sacrificeClassifier(result)) {
                    sac++;

                    if (paResult === PlateAppearanceResult.SacrificeBunt.key) {
                        sacHits++;
                    }
                    else if (paResult === PlateAppearanceResult.SacrificeFly.key) {
                        sacFlies++;
                    }
                    // so, is it bunt ?
                    else if (
                        result[ResultDataNew.Data.key][DataSection.Tagging.key]
                        && result[ResultDataNew.Data.key][DataSection.Tagging.key][ResultPair.getPitchProperty()]
                        && result[ResultDataNew.Data.key][DataSection.Tagging.key][ResultPair.getPitchProperty()][TaggingData.IsBunt.key] === true
                    ) {
                        sacHits++;
                    }
                    // so now let's check if its fly ball
                    else if (
                        result[ResultDataNew.Data.key][DataSection.Tagging.key]
                        && result[ResultDataNew.Data.key][DataSection.Tagging.key][ResultPair.getPitchProperty()]
                        && this.airBallClassifier(
                            result[ResultDataNew.Data.key][DataSection.Tagging.key][ResultPair.getPitchProperty()][TaggingData.BattedBallType.key]
                        )
                    ) {
                        sacFlies++;
                    }
                }
            }
        }

        // now include hits and total bases
        if (sacFlies > 0) {
            const sacFlyParam = this.supportedParams[0];
            output[sacFlyParam.key] = RankingStat.fromParameter(
                sacFlyParam, this.numberFormatter.format(sacFlies), sacFlies
            );
        }
        if (sacHits > 0) {
            const sacHitParam = this.supportedParams[1];
            output[sacHitParam.key] = RankingStat.fromParameter(
                sacHitParam, this.numberFormatter.format(sacHits), sacHits
            );
        }

        return output;
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default NewSacrificeStatsCalculator;